// Phase G2: design-system acceptance gallery.
//
// Visible at /qa-gallery in dev + prod. Two purposes:
//   1. Acceptance surface for the G2 PR — every shadcn primitive we
//      ship is rendered here at least once so reviewers (and the
//      e2e suite) can confirm Tailwind + Radix + token CSS variables
//      render correctly.
//   2. Reference for G3 ports — when porting storybook screens, the
//      gallery shows what the primitives look like in the pencilwise
//      cream/terracotta token system without needing the storybook
//      checkout open.
//
// NOT in the gate ladder — accessible directly at /qa-gallery in DEV
// even when the user isn't signed in (because the route bypasses the
// AppStateProvider via the App.jsx short-circuit). The bypass and this
// component are both gated behind import.meta.env.DEV (R-1), so a
// production user hitting the URL sees a "not available" message rather
// than the raw gallery.

import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { BadgeArtGallery } from "@/art/badges/BadgeArtGallery";

function Section({
  title,
  children,
  testid,
}: {
  title: string;
  children: React.ReactNode;
  testid: string;
}) {
  return (
    <section
      className="rounded-xl border border-border bg-card p-6 shadow-soft"
      data-testid={testid}
    >
      <h2 className="font-serif text-2xl mb-4 text-foreground">{title}</h2>
      <div className="flex flex-wrap items-start gap-4">{children}</div>
    </section>
  );
}

function QAGalleryRoute() {
  // R-1: belt-and-braces — even if a future router change reaches this
  // component outside the App.jsx DEV-gated bypass, never render the raw QA
  // surface in production.
  if (!import.meta.env.DEV) {
    return (
      <div className="sb-root flex min-h-[100dvh] items-center justify-center bg-background p-8 text-center">
        <p className="text-base text-muted-foreground">This page isn't available.</p>
      </div>
    );
  }
  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background font-sans text-foreground p-8">
        <header className="mb-8" data-testid="qa-gallery-header">
          <h1 className="font-serif text-4xl text-foreground">
            QA Gallery — Phase G2 design system
          </h1>
          <p className="mt-2 text-muted-foreground">
            Every shadcn primitive shipped in PR #9 is rendered here.
            E2E asserts at least 10 distinct primitives mount.
          </p>
        </header>

        <div className="grid gap-6 max-w-5xl">
          <Section title="Buttons" testid="qa-section-button">
            <Button>Default</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="destructive">Destructive</Button>
            <Button variant="link">Link</Button>
          </Section>

          <Section title="Cards" testid="qa-section-card">
            <Card className="w-72">
              <CardHeader>
                <CardTitle>Card title</CardTitle>
                <CardDescription>
                  Cream + terracotta token system rendered via CSS vars.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm">
                  Card content body — uses --card / --card-foreground.
                </p>
              </CardContent>
            </Card>
          </Section>

          <Section title="Badges" testid="qa-section-badge">
            <Badge>Default</Badge>
            <Badge variant="secondary">Secondary</Badge>
            <Badge variant="outline">Outline</Badge>
            <Badge variant="destructive">Destructive</Badge>
          </Section>

          <Section title="Avatar" testid="qa-section-avatar">
            <Avatar>
              <AvatarImage src="/nonexistent.png" alt="" />
              <AvatarFallback>PW</AvatarFallback>
            </Avatar>
          </Section>

          <Section title="Progress + Slider" testid="qa-section-progress">
            <div className="w-72 space-y-4">
              <Progress value={42} />
              <Slider defaultValue={[60]} max={100} step={1} />
            </div>
          </Section>

          <Section title="Tabs" testid="qa-section-tabs">
            <Tabs defaultValue="math" className="w-72">
              <TabsList>
                <TabsTrigger value="math">Math</TabsTrigger>
                <TabsTrigger value="reading">Reading</TabsTrigger>
              </TabsList>
              <TabsContent value="math">
                <p className="text-sm pt-2">Math tab content</p>
              </TabsContent>
              <TabsContent value="reading">
                <p className="text-sm pt-2">Reading tab content</p>
              </TabsContent>
            </Tabs>
          </Section>

          <Section title="Input + Label" testid="qa-section-input">
            <div className="w-72 space-y-2">
              <Label htmlFor="kid-name">Kid name</Label>
              <Input id="kid-name" placeholder="Sasha" />
            </div>
          </Section>

          <Section title="Dialog" testid="qa-section-dialog">
            <Dialog>
              <DialogTrigger asChild>
                <Button>Open dialog</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Dialog title</DialogTitle>
                  <DialogDescription>
                    Radix-powered modal with the cream token system.
                  </DialogDescription>
                </DialogHeader>
                <p>Dialog body content.</p>
              </DialogContent>
            </Dialog>
          </Section>

          <Section title="Sheet" testid="qa-section-sheet">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline">Open sheet</Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>Sheet title</SheetTitle>
                  <SheetDescription>
                    Slide-in panel for filters / settings.
                  </SheetDescription>
                </SheetHeader>
              </SheetContent>
            </Sheet>
          </Section>

          <Section title="Tooltip" testid="qa-section-tooltip">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline">Hover me</Button>
              </TooltipTrigger>
              <TooltipContent>Tooltip content</TooltipContent>
            </Tooltip>
          </Section>

          <Section title="Separator + ScrollArea" testid="qa-section-scrollarea">
            <div className="w-72">
              <Separator />
              <ScrollArea className="mt-4 h-24 rounded-md border p-2">
                <p className="text-sm">
                  Long scrollable content. Lorem ipsum dolor sit amet,
                  consectetur adipiscing elit. Sed do eiusmod tempor
                  incididunt ut labore et dolore magna aliqua. Ut enim
                  ad minim veniam, quis nostrud exercitation ullamco.
                </p>
              </ScrollArea>
            </div>
          </Section>

          <Section title="AspectRatio" testid="qa-section-aspectratio">
            <div className="w-72">
              <AspectRatio ratio={16 / 9} className="bg-muted rounded-md">
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  16 : 9 box
                </div>
              </AspectRatio>
            </div>
          </Section>

          <Section title="Animations (storybook tokens)" testid="qa-section-animations">
            <div className="flex items-end gap-6">
              <div className="text-4xl animate-wiggle">✏️</div>
              <div className="text-4xl animate-float-slow">📘</div>
              <div className="text-4xl animate-sparkle">✨</div>
              <div className="text-4xl animate-pop-in">⭐</div>
            </div>
          </Section>

          <Section title="Token surfaces" testid="qa-section-tokens">
            <div className="grid grid-cols-3 gap-3 text-xs text-foreground">
              <div className="rounded-md p-3 bg-cream">cream</div>
              <div className="rounded-md p-3 bg-cream-deep">cream-deep</div>
              <div className="rounded-md p-3 bg-terracotta text-primary-foreground">
                terracotta
              </div>
              <div className="rounded-md p-3 bg-forest text-accent-foreground">
                forest
              </div>
              <div className="rounded-md p-3 bg-gold">gold</div>
              <div className="rounded-md p-3 bg-pal-yellow">pal-yellow</div>
              <div className="rounded-md p-3 bg-pal-blue">pal-blue</div>
              <div className="rounded-md p-3 bg-pal-purple">pal-purple</div>
              <div className="rounded-md p-3 bg-pal-pink">pal-pink</div>
            </div>
          </Section>

          {/* MA-893 — badge art (code-drawn SVG): specimens + the full catalogue. */}
          <Section title="Badge art (MA-893)" testid="qa-section-badge-art">
            <BadgeArtGallery />
          </Section>
        </div>
      </div>
    </TooltipProvider>
  );
}

export const Route = createFileRoute("/qa-gallery")({
  component: QAGalleryRoute,
});
